import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { assert } from '@ember/debug'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type {
  RequestScope,
  ReportingQuerySort,
  ReportingQueryPage,
  BaseReport,
  BaseReportMetaPaging,
} from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { restartableTask, timeout } from 'ember-concurrency'
import { groupBy } from 'lodash'
import type { LessonItem } from 'teacher-ui-client/components/tables/skills-mastered/students-results'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type { IntlService } from 'ember-intl'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { LessonResultCategory } from 'teacher-ui-client/components/tables/skills-mastered/students-results'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

export interface ReportData extends BaseReport {
  students: {
    id: string
    first_name: string
    last_name: string
    external_id: Nullable<string>
    highest_lesson_passed: number | null
    results: Record<
      number,
      {
        lesson_results: number[]
        lesson_results_category: LessonResultCategory
      }
    >
  }[]
  meta: BaseReportMetaPaging
}

interface Params extends Record<string, unknown> {
  sort: string
  pageNumber: number
  perPage: number
}

export default class extends Route {
  @service reporting!: ReportingService

  @service lessonInfo!: LessonInfoService

  @service fileDownload!: FileDownload

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  queryParams = {
    gradePosition: {
      refreshModel: false,
      replace: true,
    },
    sort: {
      refreshModel: true,
      replace: true,
    },
    pageNumber: {
      refreshModel: true,
      replace: true,
    },
    perPage: {
      refreshModel: true,
      replace: true,
    },
  }

  /**
   * Uses data from lesson info service to return an array of normalised maths lessons
   */
  get lessonItems(): LessonItem[] {
    const lessons = this.lessonInfo.lessonsFor(Precinct.MS_LESSONS)
    return lessons.map<LessonItem>((lesson) => {
      assert(
        'lesson info required for maths skills mastered',
        typeof lesson.grade_position === 'number' && typeof lesson.lesson_position === 'number' && lesson.name,
      )

      return {
        lessonPosition: lesson.lesson_position,
        lessonName: lesson.name,
        gradePosition: lesson.grade_position,
      }
    })
  }

  buildRouteInfoMetadata() {
    return {
      exportOptions: [
        {
          exportType: ExportType.CSV,
          exportAction: () => this.exportCsv(),
        },
      ],
    }
  }

  model({ sort, pageNumber, perPage }: Params) {
    const { uiScope, period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const lessonItemsByGrade: Record<number, LessonItem[]> = groupBy(this.lessonItems, 'gradePosition')

    const page = { number: pageNumber, size: perPage }
    const requestScope = toRequestScope(uiScope)
    void this.fetchStats.perform(Product.MS, sort, page, period, requestScope).catch(() => {})

    return { lessonItemsByGrade, fetchStats: this.fetchStats }
  }

  fetchStats = restartableTask(async (...args: Parameters<typeof this.fetchReport>) => {
    await timeout(500)
    return this.fetchReport(...args)
  })

  fetchStatsForExport = restartableTask(async (...args: Parameters<typeof this.fetchReport>) => {
    await timeout(500)
    return this.fetchReport(...args)
  })

  fetchReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'skills-mastered/students-results/table'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ sort, page, period })
    return this.reporting.fetchReport<ReportData>(url, query)
  }

  /**
   * This function will be called by the export csv button at the top of the page.
   */
  async exportCsv() {
    const { uiScope, period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    const { sort } = this.paramsFor('reporting.ui-scope.maths.skills-mastered.cohort.students-results') as Params
    const page = 'all'
    const requestScope = toRequestScope(uiScope)

    let report: Awaited<ReturnType<typeof this.fetchStatsForExport.perform>>
    try {
      report = await this.fetchStatsForExport.perform(Product.MS, sort, page, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const reportData = report.students

    const lessonNumbers = this.lessonItems.map((item) => item.lessonPosition)

    const header = [
      this.intl.t('components.reporting.tables.skillsMastered.studentsResults.headers.first_name'),
      this.intl.t('components.reporting.tables.skillsMastered.studentsResults.headers.last_name'),
      this.intl.t('reporting.dashboardTables.studentExternalId'),
      this.intl.t('components.reporting.tables.skillsMastered.studentsResults.headers.highest_lesson_passed'),
      ...lessonNumbers,
    ]
    const rows = reportData.map((studentData) => {
      const results = lessonNumbers.map((lessonNumber) => {
        const lessonResult = studentData.results[lessonNumber]
        if (lessonResult?.lesson_results_category === LessonResultCategory.TestedAbove) return '-'
        return lessonResult?.lesson_results.join('|') ?? ''
      })
      return [
        studentData.first_name,
        studentData.last_name,
        studentData.external_id,
        studentData.highest_lesson_passed,
        ...results,
      ]
    })
    const filename = this.intl.t('reporting.maths.skillsMastered.cohort.studentsResults.exportCsvFilename', {
      start: this.intl.formatDate(period.start).replace(/\//g, '-'),
      end: this.intl.formatDate(period.end).replace(/\//g, '-'),
    })

    this.fileDownload.downloadAsCsv([header, ...rows], { filename, header: true })
  }
}
